import React, { useCallback, useRef, useState, DragEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import {
  initRecordingUploadAsync,
  completeRecordingUploadAsync,
  fetchRecordingsAsync,
  Recording,
  TranscriptionMode
} from '../store/slices/recordingsSlice';
import { getApiErrorMessage } from '../utils/apiError';
import {
  ENGINE_LOAD_TIMEOUT_MS,
  PROBE_EXEC_TIMEOUT_MS,
  DURATION_PROBE_TIMEOUT_MS,
  withTimeout,
  execWithWatchdog,
  type FfmpegLogCb,
  type FfmpegProgressCb,
} from '../utils/ffmpegGuards';
import './RecordingUpload.css';

type Phase = 'idle' | 'loading-engine' | 'probing' | 'transcoding' | 'uploading' | 'completing' | 'done';

// Minimal surface of the lazily-imported @ffmpeg/ffmpeg + @ffmpeg/util APIs that
// this module relies on. Avoids pulling the full types in (and avoids `any`).
interface FFmpegLike {
  on(event: 'log', cb: FfmpegLogCb): void;
  on(event: 'progress', cb: FfmpegProgressCb): void;
  off(event: 'log', cb: FfmpegLogCb): void;
  off(event: 'progress', cb: FfmpegProgressCb): void;
  load(opts: { coreURL: string; wasmURL: string; workerURL?: string }): Promise<unknown>;
  // Resolves with ffmpeg's process exit code (0 = success).
  exec(args: string[]): Promise<number>;
  writeFile(name: string, data: Uint8Array): Promise<unknown>;
  readFile(name: string): Promise<Uint8Array<ArrayBuffer>>;
  deleteFile(name: string): Promise<unknown>;
  terminate(): void;
}

interface FFmpegBundle {
  ff: FFmpegLike;
  fetchFile: (input: File | Blob | string) => Promise<Uint8Array>;
}

// 600 MB source cap. Audio-only recordings are far smaller than this; the cap
// keeps a source comfortably within the single-threaded core's ~2 GB heap (the
// input is held in the WASM FS plus a transient JS-heap copy during fetchFile).
const SOURCE_MAX_BYTES = 600_000_000;

// Lazy-loaded ffmpeg.wasm singleton. The log buffer is a sliding window of recent
// ffmpeg stderr lines so callers (e.g. `probeAudioStreamCount`) can inspect them
// after running a probe command without juggling per-call event listeners.
let ffmpegInstance: FFmpegBundle | null = null;
const FFMPEG_LOG_BUFFER_MAX = 500;
const ffmpegLogBuffer: string[] = [];
// Load the single-threaded ffmpeg.wasm core (`@ffmpeg/core`, ~2 GB MAXIMUM_MEMORY).
// Audio-only repacks fit comfortably under 2 GB, and the single-threaded core
// avoids the shared-heap "memory access out of bounds" crashes the multi-threaded
// core hit on multi-stream libopus encodes. (The mt core's 4 GB ceiling was only
// needed for the now-removed 800 MB+ video recordings.)
async function getFFmpeg(onPhase?: (p: Phase) => void): Promise<FFmpegBundle> {
  if (ffmpegInstance) return ffmpegInstance;
  onPhase?.('loading-engine');
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = new FFmpeg();
  ff.on('log', ({ message }: { message: string }) => {
    ffmpegLogBuffer.push(message);
    if (ffmpegLogBuffer.length > FFMPEG_LOG_BUFFER_MAX) ffmpegLogBuffer.shift();
    console.log('[ffmpeg]', message);
  });

  // Same-origin ESM build of @ffmpeg/core, served at /ffmpeg-core/* by the
  // ffmpegCore Vite plugin (see vite.config.ts). Must be ESM, not UMD:
  // @ffmpeg/ffmpeg's dispatcher worker is always module-type, so its load path
  // ends in `(await import(coreURL)).default` — UMD files have no ESM default.
  // No workerURL: the single-threaded core has no pthread worker file.
  try {
    await withTimeout(
      ff.load({
        coreURL: '/ffmpeg-core/ffmpeg-core.js',
        wasmURL: '/ffmpeg-core/ffmpeg-core.wasm',
      }),
      ENGINE_LOAD_TIMEOUT_MS,
      'Timed out loading the audio engine (ffmpeg.wasm). Check your connection and try again.',
    );
  } catch (e) {
    // A timed-out/failed load leaves a half-initialised worker. ffmpegInstance was
    // never assigned, so tear down this local instance directly to free the worker.
    try { ff.terminate(); } catch (_) { /* ignore */ }
    throw new Error(`Failed to load the audio engine (ffmpeg.wasm): ${describeThrown(e)}`, { cause: e });
  }

  ffmpegInstance = { ff, fetchFile } as unknown as FFmpegBundle;
  return ffmpegInstance;
}

// Terminate the current instance and drop the singleton so the next getFFmpeg()
// loads a fresh one. The single-threaded @ffmpeg/core can only run ONE exec() per
// load — a second ffmpeg run aborts the module ("Aborted()") and the exec promise
// never settles (ffmpeg.wasm #436 / #330). Since a repack needs two runs (probe,
// then encode), we reload between them; we also reset after each upload so the next
// upload's probe doesn't land on an already-spent instance.
function resetFFmpeg(): void {
  if (ffmpegInstance) {
    try { ffmpegInstance.ff.terminate(); } catch (_) { /* ignore */ }
    ffmpegInstance = null;
  }
}

function clearFFmpegLog(): void {
  ffmpegLogBuffer.length = 0;
}

function countAudioStreamsInLog(): number {
  // ffmpeg prints "Stream #0:N(lang): Audio: ..." for each audio stream when parsing input.
  const re = /Stream #\d+:\d+(?:\([^)]*\))?: Audio:/;
  return ffmpegLogBuffer.filter((l) => re.test(l)).length;
}

// Tail of the ffmpeg stderr buffer, for embedding in error messages. ffmpeg's real
// reason for a failed transcode is almost always in its last few log lines
// (codec not found, invalid data, OOM, etc.) — surface those instead of a bare fallback.
function ffmpegLogTail(maxLines = 40): string {
  const tail = ffmpegLogBuffer.slice(-maxLines).map((l) => l.trim()).filter(Boolean);
  return tail.length ? tail.join('\n') : '(no ffmpeg log output captured)';
}

// ffmpeg.wasm rejects with non-Error values (a numeric exit code, a string, or a
// raw wasm abort) as often as with Error instances — getApiErrorMessage would
// collapse all of those to "Something went wrong". Normalize to a readable string.
function describeThrown(e: unknown): string {
  if (e instanceof Error) return e.message || e.name;
  if (typeof e === 'number') return `exit code ${e}`;
  if (typeof e === 'string') return e;
  if (e == null) return 'unknown error';
  try { return JSON.stringify(e); } catch (_) { return String(e); }
}

const RecordingUpload: React.FC = () => {
  const { consultationId } = useParams<{ consultationId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [transcriptionMode, setTranscriptionMode] = useState<TranscriptionMode>('multichannel');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [existingRecording, setExistingRecording] = useState<Recording | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(0);
    setFileName('');
    setError('');
  }, []);

  const handleCancel = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    resetFFmpeg();
    reset();
  }, [reset]);

  const uploadToR2 = useCallback((uploadUrl: string, blob: Blob, mimeType: string) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', mimeType);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        xhrRef.current = null;
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText || ''}`));
      };
      xhr.onerror = () => {
        xhrRef.current = null;
        reject(new Error('Network error during upload'));
      };
      xhr.onabort = () => {
        xhrRef.current = null;
        reject(new Error('Upload aborted'));
      };
      xhr.send(blob);
    }), []);

  const handleFile = useCallback(async (file: File) => {
    setError('');
    if (file.size > SOURCE_MAX_BYTES) {
      setError('File too large. Please trim or compress in OBS first (600 MB max).');
      return;
    }
    setFileName(file.name);

    let audioBlob: Blob;
    const mimeType = 'audio/webm';
    let durationSeconds: number | null;

    try {
      await getFFmpeg(setPhase);
      setPhase('transcoding');
      audioBlob = await repackToWebm(file, transcriptionMode, setProgress);

      try {
        durationSeconds = await probeAudioDurationSeconds(audioBlob);
      } catch (_) {
        durationSeconds = null;
      }

      setPhase('uploading');
      setProgress(0);
      const init = await dispatch(initRecordingUploadAsync({
        consultationId: parseInt(consultationId!, 10),
        mimeType,
        sizeBytes: audioBlob.size,
        durationSeconds: durationSeconds ? Math.round(durationSeconds) : null,
        transcriptionMode
      })).unwrap();

      await uploadToR2(init.uploadUrl, audioBlob, mimeType);

      setPhase('completing');
      const recording = await dispatch(completeRecordingUploadAsync(init.recordingId)).unwrap();
      setPhase('done');
      navigate(`/recordings/${recording.id}`);
    } catch (err) {
      // Keep the raw thrown value (stack, non-Error payloads, embedded ffmpeg log)
      // in the console — getApiErrorMessage flattens it to a string for display.
      console.error('[RecordingUpload] upload failed:', err);
      // Server enforces one-per-consultation — if a recording slipped in between
      // page load and submit, surface a clear message instead of a raw error.
      const msg = getApiErrorMessage(err);
      if (/already has a recording/i.test(msg)) {
        // Re-fetch to surface the lock-out card.
        dispatch(fetchRecordingsAsync({ consultationId: parseInt(consultationId!, 10), limit: 1 }))
          .unwrap()
          .then((res: { recordings?: Recording[] }) => setExistingRecording((res?.recordings || [])[0] || null))
          .catch(() => { /* ignore */ });
      }
      setError(msg);
      setPhase('idle');
    }
  }, [transcriptionMode, consultationId, dispatch, navigate, uploadToR2]);

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    if (phase === 'idle') setIsDragOver(true);
  }, [phase]);
  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
  }, []);
  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
  }, []);
  const onDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
    if (phase !== 'idle') return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [phase, handleFile]);

  return (
    <div className="recording-upload">
      <div className="recording-upload__header">
        <h1>Add Recording</h1>
        <Link to={`/consultations/edit/${consultationId}`} className="recording-upload__back">
          ← Back to consultation
        </Link>
      </div>

      {existingRecording && (
        <div className="recording-upload__exists">
          <div className="recording-upload__exists-icon" aria-hidden>🎙</div>
          <div className="recording-upload__exists-body">
            <div className="recording-upload__exists-title">This consultation already has a recording.</div>
            <div className="recording-upload__exists-sub">
              Each consultation can have only one recording. View or manage it here.
            </div>
          </div>
          <button
            type="button"
            className="recording-upload__pick-btn"
            onClick={() => navigate(`/recordings/${existingRecording.id}`)}
          >
            Open recording
          </button>
        </div>
      )}

      {!existingRecording && (
      <div className="recording-upload__mode" role="tablist" aria-label="Transcription mode">
        <button
          type="button"
          role="tab"
          aria-selected={transcriptionMode === 'multichannel'}
          className={`recording-upload__mode-btn${transcriptionMode === 'multichannel' ? ' is-active' : ''}`}
          onClick={() => setTranscriptionMode('multichannel')}
          disabled={phase !== 'idle'}
        >
          <span className="recording-upload__mode-icon" aria-hidden>🎚</span>
          <span>
            <span className="recording-upload__mode-title">Multichannel <span className="recording-upload__mode-tag">Recommended</span></span>
            <span className="recording-upload__mode-sub">transcribe mic + desktop tracks separately</span>
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={transcriptionMode === 'mixed'}
          className={`recording-upload__mode-btn${transcriptionMode === 'mixed' ? ' is-active' : ''}`}
          onClick={() => setTranscriptionMode('mixed')}
          disabled={phase !== 'idle'}
          title="Slightly less reliable — use only if the mic track has noise issues (e.g. laptop speakers picked up). You can change this later and re-run."
        >
          <span className="recording-upload__mode-icon" aria-hidden>🔀</span>
          <span>
            <span className="recording-upload__mode-title">Mixed track</span>
            <span className="recording-upload__mode-sub">transcribe the mix only, split speakers via diarization</span>
          </span>
        </button>
      </div>
      )}

      {!existingRecording && phase === 'idle' && (
        <div
          className={`recording-upload__picker${isDragOver ? ' is-drag-over' : ''}`}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mka"
            className="recording-upload__file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
          <div className="recording-upload__picker-icon" aria-hidden>🎧</div>
          <div className="recording-upload__picker-title">
            Drop your audio file here
          </div>
          <div className="recording-upload__picker-or">or</div>
          <button
            type="button"
            className="recording-upload__pick-btn"
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          >
            Choose file
          </button>
          <ul className="recording-upload__hint">
            {transcriptionMode === 'multichannel' ? (
              <li>
                <strong>Multi-track Matroska Audio</strong> (<code>.mka</code>) with at least 3 audio tracks
                — Track&nbsp;1 = mix, Track&nbsp;2 = mic (teacher), Track&nbsp;3 = desktop (client).
                Extra tracks are ignored. OBS produces this when its recording output is set to audio-only.
              </li>
            ) : (
              <li>
                Any audio file (MP3, WAV, FLAC, M4A, multi-track <code>.mka</code>, …).
                Diarization splits speakers automatically on a single stream.
              </li>
            )}
            <li>Max source size: <strong>600 MB</strong>.</li>
          </ul>
        </div>
      )}

      {phase !== 'idle' && (
        <div className="recording-upload__progress">
          <div className="recording-upload__progress-head">
            <div className="recording-upload__progress-file" title={fileName}>{fileName}</div>
            {phase !== 'completing' && phase !== 'done' && (
              <button
                type="button"
                className="recording-upload__cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
            )}
          </div>
          <div className="recording-upload__phase">
            {phase !== 'done' && phase !== 'completing' && (
              <span className="recording-upload__spinner" aria-hidden />
            )}
            {phaseLabel(phase)}
          </div>
          <div className="recording-upload__bar">
            <div
              className={`recording-upload__bar-fill${phase === 'completing' || phase === 'done' ? ' is-indeterminate' : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="recording-upload__progress-pct">{progress}%</div>
        </div>
      )}

      {error && <div className="recording-upload__error">{error}</div>}
    </div>
  );
};

function phaseLabel(p: Phase): string {
  switch (p) {
    case 'loading-engine': return 'Loading audio engine (first-time only, ~30 MB)…';
    case 'probing': return 'Checking audio tracks…';
    case 'transcoding': return 'Repackaging audio tracks…';
    case 'uploading': return 'Uploading to storage…';
    case 'completing': return 'Finalizing…';
    case 'done': return 'Done';
    default: return '';
  }
}

async function probeAudioDurationSeconds(blob: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    // Some WebM containers make a media element fire neither loadedmetadata nor
    // error — it just hangs. Duration here is only an optional hint (the worker
    // gets the authoritative duration from Deepgram), so cap the wait and let the
    // caller fall back to null rather than freezing the upload.
    const timer = setTimeout(() => {
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(url);
      reject(new Error('Timed out reading audio metadata'));
    }, DURATION_PROBE_TIMEOUT_MS);
    audio.onloadedmetadata = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(audio.duration);
    };
    audio.onerror = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read audio metadata'));
    };
    audio.src = url;
  });
}

// Per-track Opus bitrate. 64k is transparent for voice content (stereo or mono);
// 3 tracks × 64k ≈ 192 kbps total for a 40-min recording (~58 MB).
const PER_TRACK_OPUS_BITRATE = '64k';

// Repackage the source into an Opus WebM. Two output shapes depending on the
// transcription mode and the source's actual stream count:
//
//   Multichannel: requires ≥3 audio streams. First 3 are emitted as 3 independent
//     Opus tracks (mix / mic / desktop). The worker reads tracks 1+2 separately
//     and sends them to Deepgram multichannel.
//   Mixed-track: any stream count. ≥3 streams → still emit 3 tracks (keeps the
//     recording forward-compatible if the user later flips to multichannel).
//     1–2 streams → emit a single-track WebM; Deepgram diarization splits speakers
//     on that one mix-equivalent stream.
//
// No amerge / pan tricks — each source track stays its own stream, so track 0 is
// always a sensible default playback stream. Every track is normalized to stereo
// 48 kHz and encoded with ffmpeg's native Opus encoder (see ENCODE below).
async function repackToWebm(file: File, mode: TranscriptionMode, onProgress: (pct: number) => void): Promise<Blob> {
  const inputName = 'input.bin';
  const outputName = 'output.webm';

  // The probe and the encode each run on their own freshly-loaded ffmpeg instance,
  // because the single-threaded core only allows one exec() per load (see resetFFmpeg)
  // — a second run on the same instance aborts. The source is read from the File once
  // per instance: writeFile *transfers* (detaches) the ArrayBuffer into the worker, so
  // the same buffer can't be reused for the second instance — we fetch it fresh.
  const probe = await getFFmpeg();

  // --- Probe: count audio streams. ffmpeg dumps the input's stream info to stderr
  //     while parsing it; we read those lines from the log buffer. No output file is
  //     given on purpose — ffmpeg prints the input streams, then exits non-zero with
  //     "At least one output file must be specified" (swallowed below). The earlier
  //     `-f null -` form additionally logged the muxer's own output stream, inflating
  //     the count by one (a 2-stream source counted as 3 and wrongly passed the
  //     multichannel check); parsing-only keeps the count equal to the real input
  //     stream count. This still consumes the probe instance's single allowed run. ---
  await probe.ff.writeFile(inputName, await probe.fetchFile(file));
  clearFFmpegLog();
  try {
    await withTimeout(probe.ff.exec(['-i', inputName]), PROBE_EXEC_TIMEOUT_MS, 'Timed out reading the audio file.');
  } catch (e) {
    // Expected: a no-output run exits non-zero. Only re-throw a genuine timeout/hang;
    // for the normal non-zero exit we just want the stream-info log lines.
    if (e instanceof Error && /Timed out/.test(e.message)) {
      resetFFmpeg();
      throw e;
    }
  }
  const audioStreamCount = countAudioStreamsInLog();
  if (audioStreamCount < 1) {
    resetFFmpeg();
    throw new Error('Source contains no audio tracks.');
  }
  if (mode === 'multichannel' && audioStreamCount < 3) {
    resetFFmpeg();
    throw new Error(
      `Multichannel transcription needs 3 separate audio tracks (mix, mic, desktop), ` +
      `but the source only has ${audioStreamCount}. Re-record with OBS audio-only multi-track output, ` +
      `or switch the transcription mode to "Mixed track" to use a single-stream file.`
    );
  }

  // --- Encode on a FRESH instance. The probe above already spent the previous
  //     instance's one allowed run, so reload before encoding or the encode aborts
  //     after finishing and the promise hangs (ffmpeg.wasm #436). ---
  resetFFmpeg();
  const { ff, fetchFile } = await getFFmpeg();
  await ff.writeFile(inputName, await fetchFile(file));

  // Encoder + channel/rate normalization shared by both output shapes.
  //   `-c:a opus -strict experimental`: ffmpeg's NATIVE Opus encoder, not the
  //     external `libopus` — libopus reliably crashes ffmpeg.wasm with
  //     "memory access out of bounds" on some inputs (ffmpeg.wasm #591), and our
  //     AAC `.mka` sources trip it every time. The native encoder is a separate
  //     code path and produces ordinary Opus, so the worker/Deepgram are unaffected.
  //   `-ac 2 -ar 48000`: force every track to stereo @ 48 kHz. The native Opus
  //     encoder only accepts mono/stereo, and an undefined/surround source layout
  //     is exactly what makes libopus fall over too — normalizing sidesteps both.
  const ENCODE = [
    '-c:a', 'opus', '-strict', 'experimental',
    '-ac', '2', '-ar', '48000',
    '-b:a', PER_TRACK_OPUS_BITRATE,
  ];
  const args = audioStreamCount >= 3
    ? [
        '-i', inputName, '-vn',
        '-map', '0:a:0', '-map', '0:a:1', '-map', '0:a:2',
        ...ENCODE,
        '-metadata:s:a:0', 'title=Mix',
        '-metadata:s:a:1', 'title=Mic',
        '-metadata:s:a:2', 'title=Desktop',
        outputName,
      ]
    : [
        '-i', inputName, '-vn',
        '-map', '0:a:0',
        ...ENCODE,
        '-metadata:s:a:0', 'title=Mix',
        outputName,
      ];
  // Keep the probe + transcode log lines that accumulated above; ffmpeg's failure
  // reason (e.g. "Decoder not found", "Invalid data", OOM) lands in this buffer.
  // resetFFmpeg() on every exit path tears down the worker (freeing its WASM FS) and
  // leaves the next upload a clean single-use instance. execWithWatchdog converts a
  // silent stall or a wasm abort-then-hang into a thrown Error so the UI can't sit
  // at 100% forever; it leaves the benign end-of-run Aborted() alone (see its doc).
  let ret: number;
  try {
    ret = await execWithWatchdog(ff, args, onProgress, ffmpegLogTail);
  } catch (e) {
    resetFFmpeg();
    // execWithWatchdog already embeds the ffmpeg log tail in its Error messages;
    // re-throw those as-is and only format non-Error throws.
    throw e instanceof Error
      ? e
      : new Error(`Audio transcode failed (${describeThrown(e)}).\nffmpeg log:\n${ffmpegLogTail()}`, { cause: e });
  }
  // A non-zero exit means ffmpeg failed — most importantly, a wasm abort the core's
  // dispatcher swallowed (it returns -1 instead of rejecting), which would otherwise
  // sail through to a corrupt/partial upload. ret=0 is the only success.
  if (ret !== 0) {
    resetFFmpeg();
    throw new Error(`Audio transcode failed (ffmpeg exit code ${ret}).\nffmpeg log:\n${ffmpegLogTail()}`);
  }

  let data: Uint8Array<ArrayBuffer>;
  try {
    data = await ff.readFile(outputName);
  } catch (e) {
    resetFFmpeg();
    throw new Error(`Audio transcode produced no output (${describeThrown(e)}).\nffmpeg log:\n${ffmpegLogTail()}`, { cause: e });
  }
  if (!data || data.length === 0) {
    resetFFmpeg();
    throw new Error(`Audio transcode produced an empty file.\nffmpeg log:\n${ffmpegLogTail()}`);
  }
  // Copy the bytes into a Blob before tearing down the instance frees the WASM FS.
  const blob = new Blob([data], { type: 'audio/webm' });
  resetFFmpeg();
  return blob;
}

export default RecordingUpload;
